<script setup lang="ts">
definePageMeta({
  title: 'Terms of Service - Collor',
  hideChrome: true,
})
</script>

<template>
  <UPageSection>
    <div class="min-h-[60vh] flex items-center justify-center px-8 py-10">
      <div
          class="max-w-3xl w-full rounded-2xl border border-white/10 bg-bush-900/60 backdrop-blur-xl shadow-2xl px-8 py-10 md:p-10 text-[#cedec2]"
      >
        <h1 class="text-2xl md:text-3xl font-semibold text-golden-grass"> Terms of Service </h1>
        <p class="text-[#cedec2] text-xs font-normal opacity-80 mb-6">last updated: 21/11/2025</p>
        <p class="text-sm mb-6 opacity-80">
          These Terms of Service (&ldquo;Terms&rdquo;) govern the use of the services and platforms provided by
          Collor BV (&ldquo;Collor&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo;). By accessing or
          using our services, you agree to be bound by these Terms.
        </p>

        <div class="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              1. Provider
            </h2>
            <p>
              Collor BV<br />
              Beverlosesteenweg&nbsp;129<br />
              3583&nbsp;Beringen, Belgium<br />
              BTW&nbsp;BE0801581076<br />
              Email:
              <a
                  href="mailto:info@collor.be"
                  class="text-golden-grass hover:text-golden-grass/80 hover:underline"
              >
                info@collor.be
              </a>
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              2. Scope of the services
            </h2>
            <p>
              Collor provides IoT-based solutions, connectivity and dashboards to support remote dispensing and
              related operational workflows for professional users, such as veterinary practices and municipalities.
              The exact scope of the services, features and service levels may be defined further in separate
              agreements, proposals or order forms between Collor and its customers.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              3. Use of the services
            </h2>
            <p class="mb-2">
              You agree to use the services only in accordance with:
            </p>
            <ul class="list-disc list-inside space-y-1">
              <li>Applicable laws and regulations.</li>
              <li>These Terms and any separate written agreement with Collor.</li>
              <li>Any reasonable instructions, technical documentation or usage guidelines we provide.</li>
            </ul>
            <p class="mt-2">
              You are responsible for maintaining the confidentiality of any login credentials and for all activity
              that occurs under your accounts.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              4. Customer responsibilities
            </h2>
            <p>
              Unless otherwise agreed, you are responsible for the correct installation, placement and day-to-day
              operation of hardware in the field, as well as for the accuracy of any data or configuration you
              provide. You are also responsible for ensuring that your use of the services and any data you input do
              not infringe the rights of third parties.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              5. Intellectual property
            </h2>
            <p>
              All intellectual property rights in and to our platforms, software, documentation, designs and
              underlying technology remain the exclusive property of Collor and/or its licensors. No rights are
              granted to you other than those expressly set out in these Terms or in a separate written agreement.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              6. Availability and changes
            </h2>
            <p>
              We aim to provide reliable and secure services, but uninterrupted availability cannot be guaranteed.
              We may update, modify or improve our services from time to time, for example to add features, improve
              performance or address security issues. If a change would materially reduce core functionality for a
              paying customer, we will use reasonable efforts to provide prior notice.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              7. Liability
            </h2>
            <p>
              To the extent permitted by applicable law, Collor is not liable for any indirect or consequential
              damages (such as loss of profit, loss of data or reputational damage) arising from the use of or
              inability to use the services. Any direct liability of Collor will be limited to the amounts paid for
              the relevant services over a defined period, as may be further specified in a separate agreement.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              8. Term and termination
            </h2>
            <p>
              The duration of subscriptions or service engagements is typically governed by separate agreements or
              order forms. Either party may terminate the services in accordance with the termination conditions set
              out therein or, where no such conditions apply, in accordance with applicable law.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              9. Governing law and disputes
            </h2>
            <p>
              These Terms and any non-contractual obligations arising out of or in connection with them are governed
              by the laws of Belgium, without regard to conflict of law principles. Disputes will be submitted to the
              competent courts in Belgium, unless mandatory law provides otherwise.
            </p>
          </section>

          <section>
            <h2 class="font-medium mb-1 text-golden-grass/90">
              10. Changes to these Terms
            </h2>
            <p>
              We may update these Terms from time to time. The updated version will be published on this website with
              a revised &ldquo;last updated&rdquo; date. Continued use of the services after changes take effect
              constitutes acceptance of the updated Terms.
            </p>
          </section>
        </div>
      </div>
    </div>
  </UPageSection>
</template>
